/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { ChangeEvent, FC, SyntheticEvent, useEffect, useMemo, useState } from "react";
import ProgressNoteForm from "components/forms/ProgressNoteForm";
import { useLocation } from "react-router-dom";
import Box from "components/generals/Box";
import Autocomplete from "components/generals/Autocomplete";

import { useTranslation } from "react-i18next";
import { AutoCompleteOption } from "types";
import {
  AppointmentInterface,
  HeaderEnum,
  PatientInterface,
  QueryResultInterface,
} from "medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import Checkbox from "components/generals/Checkbox";
import { Grid } from "@mui/material";
import HistoryProgressNoteCard from "components/views/HistoryProgressNoteCard";
import { getFromISOYYYYMMDDDateFormat } from "utils/date.utils";

/**
 * Content component for creating and managing progress notes.
 * Provides a comprehensive interface for documenting patient visits including
 * appointment selection, patient lookup, progress note form, and historical progress
 * note viewing. Supports both creating new notes and viewing/editing existing ones.
 * @returns {React.ReactElement} The progress note data content component
 */
const ProgressNoteDataContent: FC = () => {

  const [_isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();
  const location = useLocation();
  const state = (location.state as { data?: string; selected?: boolean }) || {};
  const appointmentId = state.data || null;
  const selectedData = state.selected || null;
  const [appointmentList, setAppointmentList] = useState<AppointmentInterface[]>([]);
  const [patientList, setPatientList] = useState<PatientInterface[]>([]);
  const [patientModel, setPatientModel] = useState<Partial<PatientInterface>>();
  const [appointmentModel, setAppointmentModel] = useState<AppointmentInterface>();
  const [editing, setEditing] = useState<boolean>(true);
  const [historyChecked, setHistoryChecked] = useState<boolean>(false);
  const [historyId, setHistoryId] = useState<string>("");
  const [selectedAppointment, setSelectedAppointment] = useState<string>();

  useEffect(() => {
    if (!appointmentId) {
      setPatientModel(undefined);
      setAppointmentModel(undefined);
      setSelectedAppointment(undefined);
      setHistoryId("");
      setHistoryChecked(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    sendRequest(CoreAPI_URLs.PATIENTS_GET_ALL, onGetAllPatients, "GET");
  }, []);

  const onGetAllPatients = (data: QueryResultInterface<PatientInterface>) => {
    setPatientList(data.results);
  };

  const onGetPatientAppointments = (data: QueryResultInterface<AppointmentInterface>) => {
    setAppointmentList(data.results);
    const lastAppointment = data.results.length > 0 ? data.results[data.results.length - 1] : null;
    if (lastAppointment) {
      setAppointmentModel(lastAppointment);
    }
  };

  useEffect(() => {
    if (patientModel) {
      sendRequest(CoreAPI_URLs.APPOINTMENTS_GET_ALL, onGetPatientAppointments, "GET", undefined, [
        {
          header: HeaderEnum.PATIENT_ID,
          value: patientModel.id,
        },
      ]);
    }
    setSelectedAppointment(undefined);
    setAppointmentModel(undefined);
  }, [patientModel]);

  useEffect(() => {
    if (appointmentId) {
      setSelectedAppointment(appointmentId);
      sendRequest(
        CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
        onGetAppointment,
        "GET",
        undefined,
        [
          {
            header: HeaderEnum.RESOURCE_ID,
            value: appointmentId,
          },
        ]
      );
    }
  }, [appointmentId]);

  const onGetAppointment = (appointmentGet: AppointmentInterface) => {
    if (appointmentGet) {
      setPatientModel(appointmentGet.patient);
    }
  };

  const patientOptions = useMemo<AutoCompleteOption[]>(() => {
    if (patientList.length === 0) {
      return [];
    } else {
      return patientList
        .map((a) => ({
          label: `${a.person.name} ${a.person.lastname}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [patientList]);

  const appointmentOptions = useMemo<AutoCompleteOption[]>(() => {
    if (appointmentList.length === 0) {
      return [];
    } else {
      return appointmentList
        .map((a) => ({
          label:
            ` ${getFromISOYYYYMMDDDateFormat(new Date(a.arrivalDateTime))} - ${a.provider.person.name} ${a.provider.person.lastname}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [appointmentList]);

  const progressNoteOptions = useMemo<AutoCompleteOption[]>(() => {
    if (appointmentList.length === 0) {
      return [];
    } else {
      return appointmentList
        .map((a) => ({
          label:
            ` ${getFromISOYYYYMMDDDateFormat(new Date(a.arrivalDateTime))} - ${a.reason}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [appointmentList]);

  const onSelectPatientHandler = (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutoCompleteOption
  ) => {
    if (newValue) {
      const selectedPatient = patientList.find((p) => p.id === newValue.id);
      setPatientModel(selectedPatient);
      setEditing(true);
      setHistoryId("");
    }
  };
  const onSelectAppointmenttHandler = (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutoCompleteOption
  ) => {
    if (newValue) {
      const selectedAppointment = appointmentList.find((p) => p.id === newValue.id);
      setAppointmentModel(selectedAppointment);
      setEditing(false);
      setHistoryId("");
    }
  };
  const onSelectAppointmentIdHandler = (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutoCompleteOption
  ) => {
    if (newValue) {
      const selectedAppointment = appointmentList.find((p) => p.id === newValue.id);
      setSelectedAppointment(selectedAppointment.id);
    }
  };
  const onSelectProgressNoteHandler = (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutoCompleteOption
  ) => {
    if (newValue) {
      const selectedAppointment = appointmentList.find((p) => p.id === newValue.id);
      setHistoryId(selectedAppointment.id);
    }
  };

  const historyCheckHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    setHistoryChecked(checked);
  };

  return (
    <Box>
      {!appointmentId ? (
        <Grid container spacing={4}>
          <Grid item xs={12} md={6} lg={6} id="cy-patientFormContainer">
            <Autocomplete
              name="patient.id"
              options={patientOptions}
              label={t("progressNoteForm.selectPatient")}
              formikOutside={true}
              onChange={(_e, value) => {
                if (value) {
                  onSelectPatientHandler(_e, value);
                }
              }}
              noOptionsText={t("progressNoteForm.noOptions")}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={6} id="cy-appointmentFormContainer">
            <Autocomplete
              name="appointment.id"
              options={appointmentOptions}
              label={t("progressNoteForm.selectAppointment")}
              formikOutside={true}
              onChange={(_e, value) => {
                if (value) {
                  onSelectAppointmenttHandler(_e, value);
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={7} lg={7}>
            {historyChecked && (
              <Autocomplete
                options={progressNoteOptions}
                label={t("progressNoteForm.selectPreviousProgressNote")}
                formikOutside={true}
                onChange={(_e, value) => {
                  if (value) {
                    onSelectProgressNoteHandler(_e, value);
                  }
                }}
              />
            )}
            <Checkbox
              label={t("progressNoteForm.openHistory")}
              name="minorUserChecked"
              value={historyChecked}
              checked={historyChecked}
              onChange={historyCheckHandler}
            />
          </Grid>
          {!appointmentModel && editing ? (
            <Grid item xs={12}>
              <ProgressNoteForm />
            </Grid>
          ) : (
            <>
              {historyChecked ? (
                <>
                  <Grid item xs={12} md={6} lg={6}>
                    <ProgressNoteForm appointmentId={appointmentModel.id} historyActive={true} />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={6}
                    sx={{
                      position: "sticky",
                      top: 0,
                    }}
                  >
                    <HistoryProgressNoteCard appointmentId={historyId} printbtn={true} />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <ProgressNoteForm appointmentId={appointmentModel.id} />
                </Grid>
              )}
            </>
          )}
        </Grid>
      ) : (
        <Grid container spacing={4}>
          {selectedData && (
            <Grid item xs={12}>
              <Autocomplete
                options={appointmentOptions}
                label={t("progressNoteForm.selectProgressNote")}
                formikOutside={true}
                onChange={(_e, value) => {
                  if (value) {
                    onSelectAppointmentIdHandler(_e, value);
                  }
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} md={7} lg={7}>
            {historyChecked && (
              <Autocomplete
                options={progressNoteOptions}
                label={t("progressNoteForm.selectPreviousProgressNote")}
                formikOutside={true}
                onChange={(_e, value) => {
                  if (value) {
                    onSelectProgressNoteHandler(_e, value);
                  }
                }}
              />
            )}
            <Checkbox
              label={t("progressNoteForm.openHistory")}
              name="minorUserChecked"
              value={historyChecked}
              checked={historyChecked}
              onChange={historyCheckHandler}
            />
          </Grid>
          {historyChecked ? (
            <>
              <Grid item xs={12} md={6} lg={6}>
                <ProgressNoteForm appointmentId={selectedAppointment} historyActive={true} />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                lg={6}
                sx={{
                  position: "sticky",
                  top: 0,
                }}
              >
                <HistoryProgressNoteCard appointmentId={historyId} printbtn={true} />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <ProgressNoteForm appointmentId={selectedAppointment} />
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default ProgressNoteDataContent;
